import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2 } from "lucide-react";
import { TEST_IDS } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadFormModal({ open, onOpenChange, prefillData = {} }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", scope_notes: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Invalid email address";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        ...prefillData,
      });
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setForm({ name: "", email: "", phone: "", company: "", scope_notes: "" });
        onOpenChange(false);
      }, 2000);
    } catch (err) {
      setErrors({ submit: "Failed to submit. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={TEST_IDS.leadForm.modal} className="bg-[#0F172A] border-white/[0.08] text-slate-50 sm:max-w-md">
        {success ? (
          <div className="flex flex-col items-center justify-center py-10 gap-4">
            <CheckCircle2 className="h-16 w-16 text-emerald-400" />
            <h3 className="text-xl font-heading font-bold text-slate-50">Request Submitted!</h3>
            <p className="text-slate-400 text-sm text-center">Our team will reach out within 24 hours.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-slate-50">Submit Your Inquiry</DialogTitle>
              <DialogDescription className="text-slate-400">
                Share your details and we'll get back to you with a tailored proposal.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div>
                <Label htmlFor="name" className="text-slate-300 text-sm">Full Name *</Label>
                <Input
                  id="name"
                  data-testid={TEST_IDS.leadForm.nameInput}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-[#1E293B] border-white/[0.1] text-slate-50 mt-1.5"
                  placeholder="Your full name"
                />
                {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
              </div>
              <div>
                <Label htmlFor="email" className="text-slate-300 text-sm">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid={TEST_IDS.leadForm.emailInput}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-[#1E293B] border-white/[0.1] text-slate-50 mt-1.5"
                  placeholder="you@company.com"
                />
                {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="phone" className="text-slate-300 text-sm">Phone</Label>
                  <Input
                    id="phone"
                    data-testid={TEST_IDS.leadForm.phoneInput}
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="bg-[#1E293B] border-white/[0.1] text-slate-50 mt-1.5"
                    placeholder="+91 XXXXX XXXXX"
                  />
                </div>
                <div>
                  <Label htmlFor="company" className="text-slate-300 text-sm">Company</Label>
                  <Input
                    id="company"
                    data-testid={TEST_IDS.leadForm.companyInput}
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    className="bg-[#1E293B] border-white/[0.1] text-slate-50 mt-1.5"
                    placeholder="Company name"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="notes" className="text-slate-300 text-sm">Project Notes</Label>
                <Textarea
                  id="notes"
                  data-testid={TEST_IDS.leadForm.notesInput}
                  value={form.scope_notes}
                  onChange={(e) => setForm({ ...form, scope_notes: e.target.value })}
                  className="bg-[#1E293B] border-white/[0.1] text-slate-50 mt-1.5 min-h-[80px]"
                  placeholder="Briefly describe your project..."
                />
              </div>
              {errors.submit && <p className="text-red-400 text-sm">{errors.submit}</p>}
            </div>
            <DialogFooter>
              <Button
                type="submit"
                data-testid={TEST_IDS.leadForm.submitBtn}
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#00F2FE] via-[#0EA5E9] to-[#3B82F6] text-[#060913] font-semibold hover:opacity-90"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit Request"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
